import { create } from 'zustand';
import { subscribeWithSelector, persist, createJSONStorage } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { TradeSeries, Trade } from '@/api/spec.ts';
import { offsetNumberToNumber } from '@/utils/tokenNumberUtils.ts';
import { TradeItem } from '@/feature/order_book/types.ts';
import { Side } from '@/common/constants.ts';
import { clearDataMigration, CURRENT_APP_VERSION } from '@/utils/zustand_helpers.ts';

export type LastTradesConnectionStatus = 'disconnected' | 'connecting' | 'connected';

export interface LastTradesState {
  symbol: string;
  trades: TradeItem[];
  lastUpdateId: number;
  status: LastTradesConnectionStatus;
  error?: string;
  quoteDecimals: number;
  assetDecimals: number;
  maxTrades: number;
}

export interface LastTradesActions {
  connect: (symbol: string) => void;
  disconnect: () => void;
  reset: () => void;
  setSnapshot: (snapshot: { trades: Trade[]; lastUpdateId: number }) => void;
  updateTrades: (update: TradeSeries) => void;
  setError: (error?: string) => void;
  setDecimals: (quoteDecimals: number, assetDecimals: number) => void;
  setMaxTrades: (maxTrades: number) => void;
}

export type LastTradesStore = LastTradesState & LastTradesActions;

const initialTradesData = {
  trades: [],
  error: undefined,
};

const initialState: LastTradesState = {
  symbol: '',
  ...initialTradesData,
  lastUpdateId: 0,
  status: 'disconnected',
  quoteDecimals: 1,
  assetDecimals: 4,
  maxTrades: 100,
};

function mapTradesToNormalized(trades: Trade[], quoteDecimals: number, assetDecimals: number): TradeItem[] {
  return trades
    .map((trade) => {
      const price = offsetNumberToNumber(trade.p, quoteDecimals);
      const size = offsetNumberToNumber(trade.s, assetDecimals);
      return {
        time: trade.at?.t || Date.now(),
        price,
        size,
        product: price * size,
        side: trade.sd === 1 ? Side.long : Side.short,
        blockNumber: trade.at?.b,
        txHash: trade.at?.txid ?? undefined,
      };
    })
    .reverse();
}

export const useLastTradesStore = create<LastTradesStore>()(
  persist(
    subscribeWithSelector(
      immer((set, get) => ({
        ...initialState,

        connect: (symbol: string) => {
          set((draft) => {
            Object.assign(draft, initialTradesData);
            draft.symbol = symbol;
            draft.status = 'connecting';
          });
        },

        disconnect: () => {
          set((draft) => {
            draft.status = 'disconnected';
          });
        },

        reset: () => {
          set((draft) => {
            Object.assign(draft, initialTradesData);
            draft.lastUpdateId = 0;
            draft.status = 'disconnected';
          });
        },

        setSnapshot: (snapshot) => {
          set((draft) => {
            const { quoteDecimals, assetDecimals } = draft;
            const normalizedTrades = mapTradesToNormalized(snapshot.trades, quoteDecimals, assetDecimals);
            draft.trades = normalizedTrades.slice(0, draft.maxTrades);
            draft.lastUpdateId = snapshot.lastUpdateId;
            draft.status = 'connected';
          });
        },

        updateTrades: (update: TradeSeries) => {
          const state = get();

          if (state.status !== 'connected') {
            console.log('Trades update ignored - store not connected');
            return;
          }

          set((draft) => {
            const { quoteDecimals, assetDecimals, maxTrades } = state;

            const normalizedTrades = mapTradesToNormalized(update.d, quoteDecimals, assetDecimals);

            const allTrades = [...normalizedTrades, ...draft.trades];
            draft.trades = allTrades.slice(0, maxTrades);

            if (update.sn) {
              draft.lastUpdateId = update.sn;
            }
          });
        },

        setError: (error) => {
          set((draft) => {
            draft.error = error;
            draft.status = 'disconnected';
          });
        },

        setDecimals: (quoteDecimals, assetDecimals) => {
          set((draft) => {
            draft.quoteDecimals = quoteDecimals;
            draft.assetDecimals = assetDecimals;
          });
        },

        setMaxTrades: (maxTrades) => {
          set((draft) => {
            if (maxTrades !== draft.maxTrades) {
              draft.maxTrades = maxTrades;
              // Trim trades if new max is smaller
              if (draft.trades.length > maxTrades) {
                draft.trades = draft.trades.slice(-maxTrades);
              }
            }
          });
        },
      }))
    ),
    {
      name: 'dex-last-trades',
      version: CURRENT_APP_VERSION,
      migrate: clearDataMigration,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        symbol: state.symbol,
        quoteDecimals: state.quoteDecimals,
        assetDecimals: state.assetDecimals,
        maxTrades: state.maxTrades,
      }),
    }
  )
);
